import { useEffect } from 'react';
import { create } from 'zustand';
import { zSeasonResponse, type Season } from '@tg/shared';
import { endpoints } from '@/api/endpoints';
import { configureFx } from '@/fx';

interface SeasonStore {
  season: Season | null;
  loaded: boolean;
  loading: boolean;
  load(): Promise<void>;
}

/** Current holiday (server calendar, Beijing days, admin switch). Decoration only — nothing economic reads it. */
export const useSeasonStore = create<SeasonStore>()((set, get) => ({
  season: null,
  loaded: false,
  loading: false,
  async load() {
    if (get().loading) return;
    set({ loading: true });
    let season: Season | null = null;
    try {
      season = zSeasonResponse.parse(await endpoints.season()).season;
    } catch {
      // offline / old server / bad payload → no decorations (never blocks the lobby)
      season = null;
    }
    set({ season, loaded: true, loading: false });
    configureFx({ festive: season !== null });
  },
}));

/** Current holiday or null; loads once on first use (shared by every caller). */
export function useSeason(): Season | null {
  const season = useSeasonStore((s) => s.season);
  const loaded = useSeasonStore((s) => s.loaded);
  useEffect(() => {
    if (!loaded) void useSeasonStore.getState().load();
  }, [loaded]);
  return season;
}
